import { Stack, Typography } from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoadCarrierStore } from '@/modules/loadCarriers/store/useLoadCarrierStore';
import {
  getNonShuttleIds,
  getShuttleSelectionChange,
  isShuttleLoadCarrierId,
  preserveSelectedShuttle,
} from '@/modules/setupV2/helpers/shuttleSelectionHelpers';

import { SetupPropertiesSelectDialog } from '../../common/SetupPropertiesSelectDialog';
import { LoadCarrierTypeCard } from './LoadCarrierTypeCard';
import { useLoadTypeRemovalGuard } from '@/modules/setupV2/hooks/useLoadTypeRemovalGaurd';

type Props = {
  open: boolean;
  enabledLoadCarriersIds: string[];
  onSubmit: (id: string[]) => void;
  onClose: () => void;
};

const LoadCarrierTypeSelectDialogComponent = ({
  open,
  enabledLoadCarriersIds,
  onClose,
  onSubmit,
}: Props) => {
  const { t } = useTranslation(['interface']);
  const { allLoadCarriersState: allLoadCarriers } = useLoadCarrierStore.getState();
  const [innerSelectedLoadCarriersIds, setInnerSelectedLoadCarriersIds] =
    useState<string[]>(enabledLoadCarriersIds);
  const innerSelectedLoadCarriersIdsRef = useRef(enabledLoadCarriersIds);
  const multiLoadCarrierSelectionEnabled = false; // this will at some point require some logic, used to be export user but that does not exist anymore.
  const { canRemoveLoadType, canRemoveLoadTypes } = useLoadTypeRemovalGuard();

  const { regularLoadCarriers, shuttleLoadCarriers } = useMemo(
    () => ({
      regularLoadCarriers: allLoadCarriers.filter(({ id }) => !isShuttleLoadCarrierId(id)),
      shuttleLoadCarriers: allLoadCarriers.filter(({ id }) => isShuttleLoadCarrierId(id)),
    }),
    [allLoadCarriers],
  );

  useEffect(() => {
    innerSelectedLoadCarriersIdsRef.current = enabledLoadCarriersIds;
    // plz fix me
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInnerSelectedLoadCarriersIds(enabledLoadCarriersIds);
  }, [open, enabledLoadCarriersIds]);

  const updateSelectedLoadCarrierIds = useCallback((selectedIds: string[]) => {
    innerSelectedLoadCarriersIdsRef.current = selectedIds;
    setInnerSelectedLoadCarriersIds(selectedIds);
  }, []);

  const onShuttleClick = useCallback(
    (id: string) => {
      const selectedIds = innerSelectedLoadCarriersIdsRef.current;
      const { nextSelectedIds, removedShuttleId } = getShuttleSelectionChange(selectedIds, id);

      if (removedShuttleId && !canRemoveLoadType(removedShuttleId)) {
        return;
      }

      updateSelectedLoadCarrierIds(nextSelectedIds);
    },
    [canRemoveLoadType, updateSelectedLoadCarrierIds],
  );

  const onMultiSelectClick = useCallback(
    (id: string) => {
      const selectedIds = innerSelectedLoadCarriersIdsRef.current;

      if (!selectedIds.includes(id)) {
        updateSelectedLoadCarrierIds([...selectedIds, id]);
        return;
      }

      if (!canRemoveLoadType(id)) {
        return;
      }

      updateSelectedLoadCarrierIds(selectedIds.filter((selectedId) => selectedId !== id));
    },
    [canRemoveLoadType, updateSelectedLoadCarrierIds],
  );

  const onSingleRegularClick = useCallback(
    (id: string) => {
      const selectedIds = innerSelectedLoadCarriersIdsRef.current;

      if (selectedIds.includes(id)) {
        return;
      }

      const selectedRegularIds = getNonShuttleIds(selectedIds);

      if (!canRemoveLoadTypes(selectedRegularIds)) {
        return;
      }

      updateSelectedLoadCarrierIds(preserveSelectedShuttle(selectedIds, [id]));
    },
    [canRemoveLoadTypes, updateSelectedLoadCarrierIds],
  );

  const onRegularClick = useCallback(
    (id: string) => {
      if (multiLoadCarrierSelectionEnabled) {
        // Multi select
        onMultiSelectClick(id);
        return;
      }

      // Single select
      onSingleRegularClick(id);
    },
    [multiLoadCarrierSelectionEnabled, onMultiSelectClick, onSingleRegularClick],
  );

  return (
    <SetupPropertiesSelectDialog
      title={t('setup.floorPlan.load_carrier_types_select_dialog.title')}
      innerSelectedIds={innerSelectedLoadCarriersIds}
      open={open}
      onSubmit={onSubmit}
      onClose={onClose}
    >
      <Stack flexGrow={1} overflow='auto' padding='32px 40px 40px'>
        <Stack gap={5}>
          {regularLoadCarriers.length > 0 && (
            <Stack gap={2}>
              <Typography variant='h6'>
                {t('setup.floorPlan.load_carrier_types_select_dialog.sections.load_carriers', {
                  defaultValue: 'Load carriers',
                })}
              </Typography>

              <Stack gap={4} direction='row' flexWrap='wrap'>
                {regularLoadCarriers.map((item) => (
                  <LoadCarrierTypeCard
                    key={item.id}
                    item={item}
                    selected={innerSelectedLoadCarriersIds.includes(item.id)}
                    onClick={onRegularClick}
                  />
                ))}
              </Stack>
            </Stack>
          )}

          {shuttleLoadCarriers.length > 0 && (
            <Stack gap={2}>
              <Typography variant='h6'>
                {t('setup.floorPlan.load_carrier_types_select_dialog.sections.shuttle_vehicles', {
                  defaultValue: 'Shuttle vehicles',
                })}
              </Typography>

              <Stack gap={4} direction='row' flexWrap='wrap'>
                {shuttleLoadCarriers.map((item) => (
                  <LoadCarrierTypeCard
                    key={item.id}
                    item={item}
                    selected={innerSelectedLoadCarriersIds.includes(item.id)}
                    onClick={onShuttleClick}
                  />
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Stack>
    </SetupPropertiesSelectDialog>
  );
};

export const LoadCarrierTypeSelectDialog = memo(LoadCarrierTypeSelectDialogComponent);
